import { useEffect, useRef, forwardRef, useState, useCallback, useMemo } from "react";
import { GOOGLE_MAPS_API_KEY } from "./../constants";
import { Loader } from "@googlemaps/js-api-loader";
import "./DroneMap.css";
import DroneTracker from "./DroneTracker";

import { useDispatch, useSelector } from "react-redux";
import axios from "../../lib/http/axiosConfig";
import { NDVIToggle } from "../Map/NDVIToggle";
import {
  setActiveTab,
  setToggle,
  setCellPreviewOpen,
  setDiagnosisFocus,
  clearDiagnosisFocus,
} from "../../features/TabSlice";
import { setEnergyDeviceFocus } from "../../features/FarmSlice";
import { toPath } from "../../utils/map";
import { flightOrder } from "../../utils/flightOrder";
import { setHoverCellId } from "../../features/RouteSlice";
import calendarIcon from "../../Styles/icons/calendar.png";
import { NDVIDateModal } from "../Map/NDVIDateModal";
import { toast } from "react-toastify";
import { parseUTC } from "../../utils/date";
import { diagnosisRunOptions, resolveDiagnosisUpdate } from "../../utils/diagnosisUpdates";
import { groupCropDiagnoses } from "../../utils/cropDiagnoses";
import { useDemoScenario } from "../../demo/DemoScenarioContext";
import { buildDemoCellSummaries } from "../../demo/demoAnalysis";
import { energyDeviceTooltip } from "../../utils/energyDevices";
import { useT } from "../../i18n";

const defaultCenter = { lat: 35.222172, lng: 126.847596 };
const defaultZoom = 17;
const LOCK_MAP_INTERACTION = true;
const LOCKED_ZOOM_FALLBACK = 19;
const getResponsiveZoom = (width) => {
  if (!Number.isFinite(width)) return LOCKED_ZOOM_FALLBACK;
  if (width >= 1600) return 19;
  if (width >= 1200) return 17;
  if (width >= 900) return 20;
  return 15;
};
const FARM_POLYGON_STYLE = {
  strokeColor: "#FFFFFF",
  strokeOpacity: 1,
  strokeWeight: 3,
  fillColor: "#000000",
  fillOpacity: 0.25,
  clickable: false,
  zIndex: 0,
};
const FARM_POLYGON_STYLE_ANALYSIS = {
  ...FARM_POLYGON_STYLE,
  fillColor: "#4A84C7",
  fillOpacity: 0.5,
};
const GRID_CELL_STYLE = {
  strokeColor: "#0C1620",
  strokeOpacity: 0,
  strokeWeight: 0,
  fillColor: "#000000",
  fillOpacity: 0.01,
  clickable: true,
  zIndex: 1,
};
const GRID_CELL_SELECTED_STYLE = {
  fillColor: "#39D2B4",
  fillOpacity: 0.4,
  strokeColor: "#7FF5D0",
  strokeOpacity: 0.9,
  zIndex: 2,
};
const GRID_CELL_SELECTED_STYLE_ANALYSIS = {
  ...GRID_CELL_SELECTED_STYLE,
  fillOpacity: 0.5,
  strokeOpacity: 1,
};
// 칸 등급 색. 초록·노랑·빨강 세 가지만 쓴다. 안 찍은 칸은 칠하지 않는다.
// 번진 정도를 농도로 나타내던 걸 걷었다. 한 눈에 구분이 안 되고,
// 정확한 수는 칸에 마우스를 올리면 요약에 그대로 나온다.
//
// 분석 모드에서는 지도 전체에 brightness(0.58) 필터가 걸린다
// (.drone-map.is-analysis .gm-style). 칸 색도 같이 42% 깎이므로
// 눈에 보이는 세기는 여기 적은 값보다 한참 약하다. 그만큼 올려 잡는다.
const GRADE_FILL = { HIGH: "#FF5646", MID: "#FFB52B", OK: "#3FD98F" };
// 지도 칸 등급(HIGH/MID/OK)을 문구 키(high/mid/ok)로 잇는다. 칸 진단 모달(컨택트시트)은
// 크롭 한 장의 등급(high/mid/ok)을 그대로 키로 써서 t(`map.grade.${grade}`) 로 부른다.
const GRADE_KEY = { HIGH: "high", MID: "mid", OK: "ok" };
// 크롭별 진단이 없는 응답(합성 사진 한 장짜리, 데모)에서는 칸 단위 상태를 대신 쓴다.
// 이 표가 없으면 칸이 심각이어도 격자가 "정상"으로 보인다.
const TONE_TO_GRADE = {
  danger: "high",
  warning: "mid",
  normal: "ok",
  info: "ok",
};
const CELL_FILTERS = [
  { key: "all", labelKey: "map.filter.all", cls: "" },
  { key: "high", labelKey: "map.grade.high", cls: " cip-chip--high" },
  { key: "mid", labelKey: "map.grade.mid", cls: " cip-chip--mid" },
  { key: "ok", labelKey: "map.grade.ok", cls: " cip-chip--ok" },
];
// 툴팁은 어두운 카드 위라 지도에 칠하는 색보다 한 단계 밝게 쓴다.
const GRADE_TIP_COLOR = { HIGH: "#FF6A5A", MID: "#FFC24A", OK: "#4FD69C" };

// Grad-CAM 은 크롭과 같은 파일명으로 gradcam 폴더에만 따로 올라간다.
//   실제 AI  : rgb/processed/crops/farm-45/drone-80/10943-...-002.jpg
//   목 피더 : rgb/processed/segmentation/farm-45/drone-80/10943-...-002.jpg
//   Grad-CAM: rgb/processed/gradcam/farm-45/drone-80/10943-...-002.jpg
// gcam_image_url 이 비어 있는 결과는 토글을 켜도 크롭이 그대로 다시 나온다.
// 그럴 때만 크롭 URL 에서 폴더만 바꿔 보게 한다.
const deriveGradcamUrl = (cropUrl) => {
  if (!cropUrl) return "";
  const swapped = cropUrl.replace(
    /rgb\/processed\/(?:crops?|segmentation)\//,
    "rgb/processed/gradcam/"
  );
  return swapped === cropUrl ? "" : swapped;
};
const gradeCellStyle = (summary) => {
  const color = GRADE_FILL[summary.grade];
  if (!color) return null;
  return {
    fillColor: color,
    fillOpacity: 0.5,
    strokeColor: color,
    strokeOpacity: 0.9,
    strokeWeight: 1.4,
    // 심각·주의를 정상 위로 올려 맞붙은 칸의 테두리가 가리지 않게 한다.
    zIndex: summary.grade === "OK" ? 2 : 3,
  };
};

const GRID_CELL_HOVER_STYLE = {
  fillColor: "#39D2B4",
  fillOpacity: 0.22,
  strokeColor: "#B8FFE9",
  strokeOpacity: 0.95,
  strokeWeight: 2,
  zIndex: 3,
};
const SELECTABLE_MAX_ZOOM = 22;

// 에너지 장치 마커 아이콘. 폴은 기둥, 큐브는 상자. fog 는 금색으로 크게, 끊긴 장치는 빨간색.
// 마커마다 data URI 가 따로라 그라데이션 id 가 겹쳐도 상관없다.
const buildEnergyDeviceIcon = (device, mode) => {
  const isCube = device.deviceType === "CUBE";
  const isFog = mode === "FOG" && device.role === "FOG";
  const offline = device.isConnected === false;
  const accent = offline ? "#FF5A5A" : isFog ? "#DFB56B" : "#37D5A4";
  const halo = offline
    ? "rgba(255,90,90,.45)"
    : isFog
      ? "rgba(223,181,107,.5)"
      : "rgba(74,212,168,.5)";
  const glyph = isCube
    ? `<path d="M24 11l11 6v12l-11 6-11-6V17z" fill="rgba(11,16,23,.85)" stroke="${accent}" stroke-width="2" stroke-linejoin="round"/>
       <path d="M13 17l11 6 11-6M24 23v12" fill="none" stroke="${accent}" stroke-width="2" stroke-linejoin="round"/>`
    : `<path d="M24 8v28M15 20h18M18 27h12M13 43h22" stroke="#d9fff2" stroke-width="2" stroke-linecap="round"/>
       <circle cx="24" cy="18" r="5" fill="${accent}" stroke="#0B1017" stroke-width="2"/>
       <circle cx="24" cy="18" r="1.6" fill="#ffffff"/>`;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="58" viewBox="0 0 48 58">
      <defs>
        <radialGradient id="halo" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="${halo}"/>
          <stop offset="100%" stop-color="rgba(0,0,0,0)"/>
        </radialGradient>
      </defs>
      <circle cx="24" cy="27" r="22" fill="url(#halo)"/>
      ${glyph}
    </svg>`;
  const scale = isFog ? 56 / 48 : 1;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(48 * scale, 58 * scale),
    anchor: new window.google.maps.Point(24 * scale, 46 * scale),
  };
};

// 농장에 연결된 에너지 장치(스마트폴 · 태양광큐브) 마커. 실계정 · 데모 공통.
// 좌표는 정보 탭이 /dashboard/{id}/info 를 받아 redux(farm.energyDevices)에 넣어 둔 값을 읽는다 —
// 지도가 따로 부르지 않아 호출이 늘지 않는다. 데모는 DemoScenarioContext 가 같은 자리를 채운다.
// fog 구성이면 fog 마커만 금색으로 크게 그린다. fog 와 edge 를 잇는 선은 뺐다(10-21) —
// 큐브가 밭 경계에 한 줄로 서면 선이 경계선과 겹쳐 지저분했다. 전력 흐름 표시는 받음/보냄 값이
// 들어온 뒤 다시 정한다.
// 마커를 누르면 정보 탭으로 가서 그 장치 줄을 펼친다.
// 장치 마커(900번대)는 경로선(≤6)보다 위, 드론(1000)보다 아래.
function EnergyDeviceMarkers({ mapRef, isMapReady }) {
  const dispatch = useDispatch();
  const t = useT();
  const energyDevices = useSelector((state) => state.farm.energyDevices);
  const markersRef = useRef([]);

  // 정보 탭이 5초마다 새 객체를 넣는다. 그릴 때 쓰는 값이 같으면 마커를 다시 만들지 않도록
  // 그 값만 추려 문자열로 비교한다(매번 지우고 그리면 깜빡인다).
  const signature = useMemo(
    () =>
      JSON.stringify({
        mode: energyDevices?.mode ?? "NONE",
        devices: (energyDevices?.devices ?? []).map((device) => ({
          id: device.id,
          name: device.name,
          deviceType: device.deviceType,
          role: device.role,
          latitude: device.latitude,
          longitude: device.longitude,
          isConnected: device.isConnected,
          operatingStatus: device.operatingStatus,
          batteryCharge: device.batteryCharge,
        })),
      }),
    [energyDevices]
  );

  useEffect(() => {
    const clear = () => {
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = [];
    };
    clear();
    if (!isMapReady || !mapRef.current || !window.google?.maps) return undefined;

    const { mode, devices: all } = JSON.parse(signature);
    const devices = all.filter(
      (device) =>
        Number.isFinite(Number(device.latitude)) && Number.isFinite(Number(device.longitude))
    );
    if (!devices.length) return undefined;

    const { Marker } = window.google.maps;
    const positionOf = (device) => ({ lat: Number(device.latitude), lng: Number(device.longitude) });
    const fog = mode === "FOG" ? devices.find((device) => device.role === "FOG") ?? null : null;

    devices.forEach((device) => {
      const marker = new Marker({
        map: mapRef.current,
        position: positionOf(device),
        icon: buildEnergyDeviceIcon(device, mode),
        title: energyDeviceTooltip(device, t, { mode }),
        zIndex: device === fog ? 910 : 900,
      });
      marker.addListener("click", () => {
        dispatch(setEnergyDeviceFocus({ id: device.id, at: Date.now() }));
        dispatch(setActiveTab("Information"));
      });
      markersRef.current.push(marker);
    });

    return clear;
  }, [dispatch, isMapReady, mapRef, signature, t]);

  return null;
}

const DroneMap = forwardRef((props, ref) => {
  const {
    onMapClick,
    onCellClick,
    selectedCellIds,
    mapRef,
  } = props;
  const mapElementRef = useRef(null);

  const dispatch = useDispatch();
  const t = useT();
  const { isDemo, mission, missionLogs, getDemoResultForCell } =
    useDemoScenario();
  const farmId = useSelector((state) => state.farm.farmId);
  const toggle = useSelector((state) => state.tab.toggle);
  // 데이터 탭에서 고른 촬영 회차. 지도 안 드롭다운과 같은 값을 본다.
  const diagnosisFocus = useSelector((state) => state.tab.diagnosisFocus);
  const boundary = useSelector((state) => state.farm.boundary);
  const gridCells = useSelector((state) => state.farm.gridCells);
  // AI 병해 결과가 BE 에 저장되면 WS 로 알림이 온다(useDroneSocket 이 여기에 담는다).
  const diagnosisUpdate = useSelector((state) => state.farm.diagnosisUpdate);
  const hoverCellId = useSelector((state) => state.route?.hoverCellId ?? null);
  const missionFlying = useSelector((state) => Boolean(state.drone?.control?.missionStatus));

  useEffect(() => {
    dispatch(setToggle(false));
  }, [farmId]);

  const [isMapReady, setIsMapReady] = useState(false);
  // 첫 fitBounds(농장 이동)가 끝나기 전까지 defaultCenter 가 노출되지 않도록
  // 지도 위에 덮어두는 로딩 커버의 표시 여부
  const [isFarmFitted, setIsFarmFitted] = useState(false);
  const gridPolygonsRef = useRef([]);
  const gridLinesRef = useRef([]);
  // 셀 id → 폴리곤과 기본 스타일. 미션 박스 칩 호버로 지도 셀을 강조할 때 쓴다.
  const gridPolygonByIdRef = useRef(new Map());
  const flightPathRef = useRef(null);
  const farmPathRef = useRef([]);


  // const isPointInside = (point, polygon) => {
  //   let inside = false;
  //   for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
  //     const xi = polygon[i].lat;
  //     const yi = polygon[i].lng;
  //     const xj = polygon[j].lat;
  //     const yj = polygon[j].lng;
  //     const intersect =
  //       yi > point.lng !== yj > point.lng &&
  //       point.lat < ((xj - xi) * (point.lng - yi)) / (yj - yi) + xi;
  //     if (intersect) inside = !inside;
  //   }
  //   return inside;
  // };

  const applyLockedZoom = useCallback((map) => {
    if (!map || !LOCK_MAP_INTERACTION) return;
    const responsiveZoom = getResponsiveZoom(mapElementRef.current?.offsetWidth);
    if (Number.isFinite(responsiveZoom)) {
      map.setZoom(responsiveZoom);
    }
  }, []);

  const [isDateOpen, setIsDateOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  // 데이터 탭에서 고른 회차를 날짜 이동 중에도 놓치지 않기 위한 자리.
  // 날짜가 바뀌면 아래 리셋 효과가 고른 회차를 지우는데, 그러면 그날 마지막
  // 회차가 대신 잡혀 사용자가 누른 것과 다른 결과가 뜬다.
  const pendingFocusIdRef = useRef(null);
  // 이미 처리한 포커스 요청. 같은 요청으로 두 번 움직이지 않게 한다.
  const handledFocusRef = useRef(null);
  // 진단 모드가 켜져 있는지. 회차 목록 조회는 모드와 무관하게 미리 도는데,
  // 그때 데이터 탭에 알리면 포커스가 잡히면서 모드까지 저절로 켜진다.
  // 알림은 네트워크 응답 뒤에 일어나므로 거울 값으로 읽어도 늦지 않는다.
  const toggleRef = useRef(false);
  useEffect(() => {
    toggleRef.current = toggle;
  }, [toggle]);
  const [isCellImageOpen, setIsCellImageOpen] = useState(false);
  const [isCellPreviewOpen, setIsCellPreviewOpen] = useState(false);
  const [cellImageUrl, setCellImageUrl] = useState("");
  const [cellThumbUrl, setCellThumbUrl] = useState("");
  const [isCellImageLoading, setIsCellImageLoading] = useState(false);
  const [cellPreviewList, setCellPreviewList] = useState([]);
  const [cellPreviewIndex, setCellPreviewIndex] = useState(0);
  const [isGradCam, setIsGradCam] = useState(false);
  const [cellStatusLabel, setCellStatusLabel] = useState("");
  const [cellStatusNote, setCellStatusNote] = useState("");
  const [cellStatusTone, setCellStatusTone] = useState("warning");
  const [cellCoordText, setCellCoordText] = useState("");
  // 어느 칸을 열었는지. 모달 머리에 "48번 칸"으로 적는다 — 예전에는 화면 어디에도
  // 없어서 사진만 보고는 어느 칸인지 알 수 없었다.
  const [cellNo, setCellNo] = useState(null);
  // 등급 칩(all/high/mid/ok)과 한 장 크게 보기 여부.
  const [cellFilter, setCellFilter] = useState("all");
  const [isCellZoomOpen, setIsCellZoomOpen] = useState(false);
  // 사진 목록의 스크롤바는 평소에는 감추고, 사용자가 목록을 움직이는 동안만 보여준다.
  const [isCellGridScrolling, setIsCellGridScrolling] = useState(false);
  const cellGridScrollTimerRef = useRef(null);
  const [selectedMissionLogId, setSelectedMissionLogId] = useState(null);
  const [selectedMissionTime, setSelectedMissionTime] = useState("");
  const [missionDates, setMissionDates] = useState(new Set());
  const hasMissionDates = missionDates.size > 0;
  const [missionTimeList, setMissionTimeList] = useState([]);
  const missionSelectionRequestRef = useRef(0);
  const cellStatusRequestRef = useRef(0);
  const manualMissionSelectionRef = useRef(false);
  // A response may only update the view from which its request was issued.
  const diagnosisViewRef = useRef(null);
  const previousView = diagnosisViewRef.current;
  if (
    !previousView || previousView.farmId !== farmId ||
    previousView.date !== selectedDate.getTime() ||
    previousView.missionId !== selectedMissionLogId
  ) {
    diagnosisViewRef.current = {
      farmId, date: selectedDate.getTime(), missionId: selectedMissionLogId,
    };
  }
  const invalidateDiagnosisRequests = useCallback(() => {
    missionSelectionRequestRef.current += 1;
    cellStatusRequestRef.current += 1;
  }, []);
  const lockMissionSelection = useCallback(() => {
    manualMissionSelectionRef.current = true;
    missionSelectionRequestRef.current += 1;
  }, []);
  useEffect(() => () => invalidateDiagnosisRequests(), [invalidateDiagnosisRequests]);
  useEffect(() => {
    if (!toggle) manualMissionSelectionRef.current = false;
  }, [toggle, farmId]);
  const [isTimeListOpen, setIsTimeListOpen] = useState(false);
  const [hasAppliedDate, setHasAppliedDate] = useState(false);
  const [analysisCellIds, setAnalysisCellIds] = useState([]);
  // 칸별 등급과 장 수. 색칠과 호버 요약을 한 번의 조회로 끝내려고 같이 받아 둔다.
  const [cellGradeMap, setCellGradeMap] = useState({});
  // 등급 경계는 서버 설정값이다. 카메라가 바뀌면 서버에서만 조정하면 된다.
  // 서버(app.diagnosis.*)가 내려주는 값으로 덮인다. 여기 기본값은 응답이 오기 전 잠깐 쓰는 값이라 서버와 같게 둔다.
  const [gradeThresholds, setGradeThresholds] = useState({ high: 80, mid: 51 });
  // 등급 요약 툴팁. 구글 InfoWindow 는 .gm-style 안에 들어가서 분석 모드의
  // 화면을 어둡게 주는 필터를 그대로 뒤집어써 글씨가 죽는다. 필터 밖에 직접 그린다.
  const [gradeTip, setGradeTip] = useState(null);
  // 비행 순서 툴팁 ("3 / 8번째"). 지도 셀 호버와 미션 박스 칩 호버 모두에서 뜬다.
  const [orderTip, setOrderTip] = useState(null);
  const projectionOverlayRef = useRef(null);
  // 칸에서 툴팁으로 넘어가는 찰나에도 mouseout 이 터진다.
  // 바로 닫으면 X 를 누를 틈이 없어서 잠깐만 유예를 둔다.
  const gradeTipHideTimerRef = useRef(null);
  const [isAnalysisAvailable, setIsAnalysisAvailable] = useState(false);

  useEffect(() => {
    dispatch(setCellPreviewOpen(isCellPreviewOpen));
    return () => dispatch(setCellPreviewOpen(false));
  }, [isCellPreviewOpen, dispatch]);

  const handleCellGridScroll = useCallback(() => {
    setIsCellGridScrolling(true);
    if (cellGridScrollTimerRef.current) {
      clearTimeout(cellGridScrollTimerRef.current);
    }
    cellGridScrollTimerRef.current = setTimeout(() => {
      setIsCellGridScrolling(false);
      cellGridScrollTimerRef.current = null;
    }, 700);
  }, []);

  useEffect(() => {
    if (isCellPreviewOpen) return undefined;
    if (cellGridScrollTimerRef.current) {
      clearTimeout(cellGridScrollTimerRef.current);
      cellGridScrollTimerRef.current = null;
    }
    setIsCellGridScrolling(false);
    return undefined;
  }, [isCellPreviewOpen]);

  useEffect(
    () => () => {
      if (cellGridScrollTimerRef.current) {
        clearTimeout(cellGridScrollTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!farmId) return;
  }, [farmId]);

  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!toggle) return;
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, [toggle]);

  const formatNow = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${y}.${m}.${day} ${hh}:${mm}:${ss}`;
  };

  const formatSelectedDate = (d) => {
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}.${m}.${day}`;
  };

  const formatTime = (d) => {
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  };

  const formatDateKey = (d) => {
    const local = new Date(d);
    local.setMinutes(local.getMinutes() - local.getTimezoneOffset());
    return local.toISOString().slice(0, 10);
  };

  useEffect(() => {
    if (!isDemo || !mission?.completedAt) return;
    setSelectedDate(new Date(mission.completedAt));
  }, [isDemo, mission?.completedAt]);

  // 데모는 서버 mission-log 대신 로컬 기록을 쓴다. 미션이 끝나는 즉시
  // 목록이 갱신되도록 fetch 가 아니라 상태 변화에 반응시킨다.
  const demoLogItems = useMemo(() => {
    if (!isDemo) return [];
    const targetDate = formatDateKey(selectedDate);
    return missionLogs
      .filter((log) => formatDateKey(log.completedAt) === targetDate)
      .map((log) => {
        const completedDate = new Date(log.completedAt);
        return {
          id: log.id,
          time: formatTime(completedDate),
          label: `${formatSelectedDate(completedDate)} ${formatTime(completedDate)}`,
          completedAt: completedDate,
        };
      })
      .sort((a, b) => a.completedAt - b.completedAt);
  }, [isDemo, missionLogs, selectedDate]);

  useEffect(() => {
    if (!isDemo) return;
    setMissionTimeList(demoLogItems);
    // 새 미션이 끝나면 그 회차를 자동 선택한다(같은 날 여러 건이면 가장 최근).
    setSelectedMissionLogId(
      demoLogItems.length ? demoLogItems[demoLogItems.length - 1].id : null
    );
  }, [isDemo, demoLogItems]);

  useEffect(() => {
    if (!isDemo) return;
    const found = demoLogItems.find((item) => item.id === selectedMissionLogId);
    setSelectedMissionTime(found?.time ?? "");
  }, [isDemo, demoLogItems, selectedMissionLogId]);

  // [지도 → 데이터 탭] 지도가 회차를 바꿨다고 알린다.
  // 회차 목록 조회, 새 진단 알림, 시간 드롭다운에서 선택이 바뀔 때 함께 알린다.
  // 상태를 보고 뒤늦게 맞추는 효과로 두면, 요청이 반영되기 전 렌더에서 옛 회차를
  // 되돌려 보내 데이터 탭 표시가 한 번 튄다. 그래서 바꾸는 그 자리에서 같이 알린다.
  const announceFocus = useCallback(
    (missionLogId, date, time) => {
      if (missionLogId == null) return;
      // 모드가 꺼져 있으면 알리지 않는다. 알리면 포커스가 잡히면서 모드까지
      // 켜져, 페이지를 열자마자 지도가 진단 화면으로 바뀐다.
      if (!toggleRef.current) return;
      dispatch(
        setDiagnosisFocus({
          missionLogId,
          date: formatDateKey(date),
          time: time ?? "",
          at: Date.now(),
          // 지도가 보낸 값이라는 표시. 이게 붙은 요청은 지도가 다시 따라가지 않는다.
          source: "map",
        })
      );
    },
    [dispatch]
  );

  // 고른 날짜에 회차가 없으면 데이터 탭 표시도 내린다. 모드는 건드리지 않는다 —
  // 지도는 "비행 데이터가 없습니다"를 띄운 채 그대로 있어야 한다.
  const dropFocus = useCallback(() => {
    if (!toggleRef.current) return;
    dispatch(clearDiagnosisFocus());
  }, [dispatch]);

  // options.selectId: 데이터 탭에서 고른 회차가 목록에 있으면 그걸 고른다.
  // 없으면 가장 최근 회차를 고른다.
  // options.date: 이 날짜로 목록을 받는다. 안 주면 화면에 적용된 날짜를 쓴다.
  // (데이터 탭에서 다른 날 회차를 고르면 selectedDate 상태가 반영되기 전에 불러야 한다.)
  // options.keepPreferred: selectId 가 목록에 없어도 그 회차를 그대로 둔다.
  //   이 목록은 완료(mission_status=2) 미션만 담는데, 조회 탭 회차 목록은 진단 결과만
  //   있으면 담는다. 그래서 중단된 미션의 회차는 여기 없을 수 있다.
  //   그때 그날 마지막 회차로 대신 잡으면 사용자가 누른 것과 다른 회차가 조용히 뜬다.
  //   오버레이(cell-status)는 회차 id 만 있으면 그리므로 선택은 건드리지 않는다.
  const fetchMissionLogForDate = useCallback(async (options = {}) => {
    const preferId = options?.selectId ?? null;
    if (!farmId) return null;
    const targetDate = formatDateKey(options?.date ?? selectedDate);
    if (isDemo) {
      return demoLogItems.length
        ? demoLogItems[demoLogItems.length - 1].id
        : null;
    }
    const requestId = ++missionSelectionRequestRef.current;
    const { data } = await axios.get(`/api/farms/${farmId}/mission-logs`, {
      params: { date: targetDate },
    });
    // 회차를 연달아 누르면 요청이 겹친다. 먼저 누른 쪽 응답이 늦게 오면
    // 나중에 고른 회차를 덮어쓰므로, 지나간 요청이면 여기서 버린다.
    if (
      options?.isStale?.() || farmId !== diagnosisViewRef.current.farmId ||
      requestId !== missionSelectionRequestRef.current
    ) return null;
    const keepPreferred = preferId != null && options?.keepPreferred === true;
    const content = data?.data?.content ?? [];
    if (!content.length) {
      setMissionTimeList([]);
      if (keepPreferred) return preferId;
      setSelectedMissionLogId(null);
      setSelectedMissionTime("");
      dropFocus();
      return null;
    }
    const matched = content
      .map((item) => ({
        id: item.id,
        date: parseUTC(item.missionDate),
      }))
      .filter((item) => Number.isFinite(item.date?.getTime?.()));

    if (!matched.length) {
      setMissionTimeList([]);
      if (keepPreferred) return preferId;
      setSelectedMissionLogId(null);
      setSelectedMissionTime("");
      dropFocus();
      return null;
    }

    matched.sort((a, b) => a.date - b.date);
    const times = matched.map((item) => ({
      id: item.id,
      time: formatTime(item.date),
      label: `${formatSelectedDate(item.date)} ${formatTime(item.date)}`,
    }));
    setMissionTimeList(times);

    // 기본은 가장 최근 회차. 예전엔 matched[0](그날 첫 비행)이라, 하루에 여러 번 날린 날은
    // 방금 끝난 비행 결과를 보려면 사용자가 시간을 직접 바꿔야 했다.
    const preferred =
      preferId == null ? null : matched.find((item) => Number(item.id) === Number(preferId));
    // 고른 회차가 이 목록에 없으면 선택은 그대로 두고 목록만 갱신한다.
    if (!preferred && keepPreferred) return preferId;
    const target = preferred ?? matched[matched.length - 1];
    setSelectedMissionLogId(target.id);
    setSelectedMissionTime(formatTime(target.date));
    announceFocus(target.id, options?.date ?? selectedDate, formatTime(target.date));
    return target.id;
  }, [farmId, isDemo, demoLogItems, selectedDate, announceFocus, dropFocus]);

  // 아래 포커스 효과는 이 함수를 의존성으로 걸 수 없다. 효과가 날짜를 바꾸면
  // 이 함수의 정체성이 따라 바뀌고, 그러면 효과가 스스로를 정리(요청 취소)하면서
  // 방금 보낸 조회를 버린다. 날짜는 인자로 넘기므로 최신 참조만 있으면 된다.
  const fetchMissionLogForDateRef = useRef(fetchMissionLogForDate);
  useEffect(() => {
    fetchMissionLogForDateRef.current = fetchMissionLogForDate;
  }, [fetchMissionLogForDate]);

  // [조회는 진단 버튼과 무관] 분석 데이터는 진단 버튼과 상관없이 미리 받아 둔다.
  // 예전엔 버튼을 켜야 조회가 시작돼 누른 뒤 기다려야 했고, 켜 둔 상태에서 새 결과가
  // 들어와도 껐다 켜거나 날짜를 다시 적용해야 보였다. 버튼은 "표시" 스위치로만 쓴다.
  useEffect(() => {
    if (!farmId) {
      setMissionDates(new Set());
      return;
    }
    if (isDemo) {
      setMissionDates(
        new Set(missionLogs.map((log) => formatDateKey(log.completedAt)))
      );
      return;
    }
    const fetchMissionDates = async () => {
      try {
        const { data } = await axios.get(`/api/farms/${farmId}/mission-dates`);
        const list = Array.isArray(data?.data) ? data.data : [];
        setMissionDates(new Set(list.filter(Boolean)));
      } catch {
        setMissionDates(new Set());
      }
    };
    fetchMissionDates();
  }, [farmId, isDemo, missionLogs]);

  // NDVI 를 켰는데 고른 날짜에 미션이 없으면 "No mission logs" 만 뜨고 끝났다.
  // 오늘이 기본값이라 며칠 전에 찍은 밭은 사용자가 날짜를 직접 뒤져야 했다.
  // 미션이 있는 가장 최근 날짜로 옮겨 준다.
  useEffect(() => {
    if (missionDates.size === 0) return;
    // 데이터 탭에서 고른 회차를 적용하는 중이면 날짜를 옮기지 않는다.
    // 이 목록은 완료 미션의 날짜만 담아서, 중단된 미션의 회차를 고르면
    // "미션 없는 날"로 읽혀 방금 고른 회차를 밀어낸다.
    if (pendingFocusIdRef.current != null) return;
    const key = formatDateKey(selectedDate);
    if (missionDates.has(key)) return;

    const latest = Array.from(missionDates).sort().pop();
    if (!latest) return;
    const [y, mo, d] = latest.split("-").map(Number);
    if (!y || !mo || !d) return;

    setSelectedDate(new Date(y, mo - 1, d));
    setSelectedMissionLogId(null);
    setSelectedMissionTime("");
    setMissionTimeList([]);
    setHasAppliedDate(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionDates, selectedDate]);

  const fetchAnalysisCellIds = useCallback(async () => {
    if (!farmId) return;
    const view = diagnosisViewRef.current;
    const requestId = ++cellStatusRequestRef.current;
    const isCurrent = () =>
      view === diagnosisViewRef.current && requestId === cellStatusRequestRef.current;
    if (isDemo) {
      // 선택된 회차가 없으면 방금 끝난 미션 결과로 보여준다.
      const log =
        missionLogs.find((item) => item.id === selectedMissionLogId) ??
        (mission?.phase === "complete" ? mission : null);
      const results = log?.results ?? [];
      // 실계정 /cell-status 와 같은 모양을 만들어 준다. 이걸 안 채우면 칸이 등급색으로
      // 칠해지지 않고, 지도 한가운데 "비행 데이터 없음" 오버레이가 뜬다.
      const { cells, highCellIds, thresholds } = buildDemoCellSummaries(results);
      // analysisCellIds 는 실계정에서 redCellIds(심각 칸)다. 여기에 전체 칸을 넣으면
      // 분석된 칸이 모두 선택 테두리를 뒤집어써서 등급색이 묻힌다.
      setAnalysisCellIds(highCellIds);
      setCellGradeMap(cells);
      setGradeThresholds(thresholds);
      setIsAnalysisAvailable(results.length > 0);
      // 이걸 켜야 날짜 옆에 회차 시각이 같이 찍힌다("9월 16일 14:12").
      // 실계정은 날짜를 적용할 때 켜지는데, 데모는 심은 회차가 이미 오늘 날짜라
      // 그 경로를 안 타서 시각이 빠진 채로 남는다.
      if (results.length) setHasAppliedDate(true);
      return;
    }
    try {
      const missionLogId =
        selectedMissionLogId ?? (await fetchMissionLogForDate());
      if (!isCurrent()) return;
      if (!missionLogId) {
        setAnalysisCellIds([]);
        setCellGradeMap({});
        setIsAnalysisAvailable(false);
        return;
      }

      const { data } = await axios.get(
        `/api/farms/${farmId}/missions/${missionLogId}/cell-status`
      );
      if (!isCurrent()) return;
      const payload = data?.data ?? {};
      const ids = payload.redCellIds ?? [];
      const nextGrades = {};
      (payload.cells ?? []).forEach((c) => {
        if (c?.gridCellId == null) return;
        nextGrades[c.gridCellId] = c;
      });
      setAnalysisCellIds(ids);
      setCellGradeMap(nextGrades);
      setGradeThresholds({
        high: payload.threshold ?? 80,
        mid: payload.midThreshold ?? 51,
      });
      setIsAnalysisAvailable(true);
    } catch (error) {
      if (!isCurrent()) return;
      console.error("Failed to load cell status:", error);
      setAnalysisCellIds([]);
      setCellGradeMap({});
      setIsAnalysisAvailable(false);
    }
  }, [
    farmId,
    fetchMissionLogForDate,
    isDemo,
    mission,
    missionLogs,
    selectedMissionLogId,
  ]);

  useEffect(() => {
    fetchAnalysisCellIds();
  }, [selectedDate, fetchAnalysisCellIds]);

  // AI results can arrive before landing. Look up the exact mission among
  // diagnosis runs, not the completed-mission-only calendar list.
  const handledDiagnosisRef = useRef(null);
  // 이미 "새 결과" 토스트를 띄운 (농장:회차). AI 집계기가 60초 배치마다 웹훅을 보내고 BE 가
  // 배치당 한 번씩 알리므로, 회차 하나에 이벤트가 여러 번 온다. 회차 목록 갱신은 매번 하되
  // 토스트는 회차당 한 번만 띄운다. 사용자가 그 회차를 열어 보면 기억을 지운다.
  const notifiedDiagnosisRef = useRef(new Set());
  // [알림 묶기] AI 가 밀린 결과를 한꺼번에 넘기면 회차마다 알림이 와서 같은 문구 토스트가 겹쳐 쌓인다.
  // 짧은 창(NOTIFY_BURST_MS) 안에 온 알림은 모아 두었다가 토스트 하나로 띄운다. 한 건이면 원래 문구,
  // 여럿이면 개수를 적는다. 토스트 id 를 농장 단위로 고정해 창이 겹쳐도 한 장만 남는다.
  const NOTIFY_BURST_MS = 3_000;
  const pendingNotifyRef = useRef({ timer: null, messages: [] });
  const queueDiagnosisNotify = useCallback(
    (message) => {
      const pending = pendingNotifyRef.current;
      pending.messages.push(message);
      if (pending.timer) return;
      pending.timer = setTimeout(() => {
        const messages = pending.messages;
        pending.messages = [];
        pending.timer = null;
        if (!messages.length) return;
        const text =
          messages.length === 1
            ? messages[0]
            : t("map.diagnosis.newResultBatch", { count: messages.length });
        toast.info(text, { toastId: `diagnosis-${farmId}` });
      }, NOTIFY_BURST_MS);
    },
    [farmId, t]
  );
  useEffect(
    () => () => {
      const pending = pendingNotifyRef.current;
      if (pending.timer) clearTimeout(pending.timer);
      pending.timer = null;
      pending.messages = [];
    },
    []
  );
  useEffect(() => {
    if (selectedMissionLogId == null) return;
    notifiedDiagnosisRef.current.delete(`${farmId}:${Number(selectedMissionLogId)}`);
  }, [farmId, selectedMissionLogId]);
  useEffect(() => {
    if (!diagnosisUpdate || diagnosisUpdate === handledDiagnosisRef.current) return;
    handledDiagnosisRef.current = diagnosisUpdate;

    if (isDemo || !farmId) return;
    if (Number(diagnosisUpdate.farmId) !== Number(farmId)) return;
    const updatedMissionLogId = Number(diagnosisUpdate.missionLogId);
    if (!Number.isSafeInteger(updatedMissionLogId) || updatedMissionLogId <= 0) return;
    const view = diagnosisViewRef.current;
    const requestId = ++missionSelectionRequestRef.current;
    if (Number(view.missionId) === updatedMissionLogId) {
      fetchAnalysisCellIds();
      return;
    }

    let cancelled = false;
    const isStale = () => cancelled || view !== diagnosisViewRef.current ||
      requestId !== missionSelectionRequestRef.current;
    const date = formatDateKey(selectedDate);
    (async () => {
      try {
        const { data } = await axios.get(`/api/dashboard/${farmId}/diagnosis/runs`, {
          params: { from: date, to: date },
        });
        if (isStale()) return;
        const runs = data?.data?.runs ?? [];
        const decision = resolveDiagnosisUpdate({
          runs, updatedMissionId: updatedMissionLogId, currentMissionId: view.missionId,
          selectedDate: date, selectionLocked: manualMissionSelectionRef.current,
        });
        const items = diagnosisRunOptions(runs, date);
        setMissionTimeList((previous) => {
          const merged = new Map(items.map((item) => [Number(item.id), item]));
          previous.forEach((item) => merged.set(Number(item.id), item));
          return Array.from(merged.values()).sort((a, b) => Number(a.id) - Number(b.id));
        });
        if (items.length) setMissionDates((previous) => new Set([...previous, date]));
        if (decision.type === "select") {
          setSelectedMissionLogId(decision.item.id);
          setSelectedMissionTime(decision.item.time);
          setHasAppliedDate(true);
          announceFocus(decision.item.id, selectedDate, decision.item.time);
        } else if (decision.type === "refresh") {
          fetchAnalysisCellIds();
        } else if (decision.type === "notify") {
          const notifyKey = `${farmId}:${updatedMissionLogId}`;
          if (!notifiedDiagnosisRef.current.has(notifyKey)) {
            notifiedDiagnosisRef.current.add(notifyKey);
            queueDiagnosisNotify(decision.message);
          }
        }
      } catch (error) {
        if (isStale()) return;
        console.error("Failed to load the updated diagnosis run:", error);
        toast.warn(t("map.toast.updateFetchFailed"), {
          toastId: `diagnosis-error-${farmId}`,
        });
      }
    })();
    return () => { cancelled = true; };
    // t 는 일부러 뺀다. 언어 전환마다 이 효과가 정리되면 진행 중인 회차 조회가 취소된다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagnosisUpdate, farmId, isDemo]);

  const openCellImage = useCallback(
    async (cell) => {
      if (!toggle || !cell?.id || !farmId) return;
      setIsCellImageLoading(true);
      setIsCellPreviewOpen(false);
      // 다른 칸을 열 때 앞 칸에서 걸어 둔 필터나 확대가 남아 있으면 안 된다.
      setCellNo(cell.id);
      setCellFilter("all");
      setIsCellZoomOpen(false);
      try {
        if (isDemo) {
          const result = getDemoResultForCell(cell.id, selectedMissionLogId);
          if (!result) {
            toast.warn(t("map.toast.noCellResult"));
            return;
          }
          setCellPreviewList(
            result.photos?.length
              ? result.photos
              : [{ crop: result.crop, gradcam: result.gradcam }]
          );
          setCellPreviewIndex(0);
          setIsGradCam(false);
          setCellImageUrl(result.crop);
          setCellThumbUrl(result.crop);
          setCellStatusLabel(`${result.label} (${result.confidence}%)`);
          setCellStatusNote(result.summary);
          setCellStatusTone(result.tone);
          setCellCoordText("");
          // 데모: 지도를 가리는 전체화면 원본 이미지 대신 Grad-CAM 패널을 바로 연다.
          setIsCellImageOpen(false);
          setIsCellPreviewOpen(true);
          return;
        }
        const missionLogId =
          selectedMissionLogId ?? (await fetchMissionLogForDate());
        if (!missionLogId) return;

        const { data } = await axios.get(
          `/api/farms/${farmId}/mission-logs/${missionLogId}/grid-cells/${cell.id}/diagnosis-results`,
          {
            headers: { "Content-Type": "application/json" },
          }
        );

        if (data?.success === false || data?.isAvailable === false) {
          setCellImageUrl("");
          setCellThumbUrl("");
          setCellPreviewList([]);
          setCellPreviewIndex(0);
          setIsGradCam(false);
          setCellStatusLabel("");
          setCellStatusNote("");
          setCellStatusTone("warning");
          setCellCoordText("");
          toast.warn(t("map.toast.noImages"));
          return;
        }

        const payload = data?.data ?? {};
        const segments = payload.segments ?? [];
        const photoUrl = payload.photo?.imageUrl || "";
        // 병명·확률·구분은 서버가 이미 보내주는데 지금까지 화면에서 버리고 있었다.
        const previewItems = groupCropDiagnoses(segments
          .map((segment) => ({
            cropObjectKey: segment.cropObjectKey,
            crop: segment.cropImageUrl || "",
            gradcam:
              segment.gradcamImageUrl || (segment.cropObjectKey ? "" : deriveGradcamUrl(segment.cropImageUrl)),
            name: segment.diseaseName || "",
            conf: segment.confidence,
            category: segment.category || "",
          }))
          .filter((item) => item.crop || item.gradcam));

        if (!previewItems.length && photoUrl) {
          previewItems.push({ crop: photoUrl, gradcam: "" });
        }

        // 첫 화면은 합성 이미지(photo.imageUrl), 세부는 개별 이미지(segments)
        const imageUrl = photoUrl || (previewItems[0]?.crop ?? "");
        const statusLabel =
          payload?.diagnosis?.label ||
          payload?.statusLabel ||
          payload?.diseaseLabel ||
          "";
        const statusNote =
          payload?.diagnosis?.summary ||
          payload?.statusNote ||
          payload?.diseaseNote ||
          "";
        const statusTone =
          payload?.diagnosis?.tone ||
          payload?.statusTone ||
          payload?.tone ||
          "";
        const coordText =
          payload?.photo?.coordText || payload?.coordText || "";

        const showNoImage = () => {
          setCellImageUrl("");
          setCellThumbUrl("");
          setCellPreviewList([]);
          setCellPreviewIndex(0);
          setIsGradCam(false);
          setCellStatusLabel("");
          setCellStatusNote("");
          setCellStatusTone("warning");
          setCellCoordText("");
          toast.warn(t("map.toast.noImages"));
        };

        if (!imageUrl) {
          showNoImage();
          return;
        }

        const imageReachable = await new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve(true);
          img.onerror = () => resolve(false);
          img.src = imageUrl;
        });

        if (!imageReachable) {
          showNoImage();
          return;
        }

        const normalizedTone = (() => {
          if (statusTone) return statusTone;
          if (statusLabel.includes("위험")) return "danger";
          if (statusLabel.includes("정상")) return "normal";
          if (statusLabel.includes("주의")) return "warning";
          if (statusLabel.includes("확률")) return "danger";
          return "warning";
        })();

        setCellPreviewList(previewItems);
        setCellPreviewIndex(0);
        setIsGradCam(false);
        setCellImageUrl(photoUrl);
        setCellThumbUrl(previewItems[0]?.crop || photoUrl);
        setCellStatusLabel(statusLabel || "");
        setCellStatusNote(statusNote);
        setCellStatusTone(normalizedTone);
        setCellCoordText(coordText);
        // 데모와 같게: 지도를 가리는 합성 원본 사진 단계를 건너뛰고 진단 모달을 바로 열다.
        setIsCellImageOpen(false);
        setIsCellPreviewOpen(true);
      } catch (error) {
        console.error("Failed to load cell image:", error);
        toast.warn(t("map.toast.noImages"));
      } finally {
        setIsCellImageLoading(false);
      }
    },
    [toggle, farmId, fetchMissionLogForDate, getDemoResultForCell, isDemo, selectedMissionLogId, t]
  );

  // [데이터 탭 → 지도] 조회 탭에서 촬영 회차를 누르면 그 회차를 지도에 띄운다.
  // 지도 안 시간 드롭다운으로 고르는 것과 같은 자리에 값을 넣을 뿐이라,
  // 오버레이를 그리는 경로는 기존 그대로다.
  useEffect(() => {
    // 지도가 보낸 알림은 이미 지도에 반영된 값이라 따라갈 것이 없다.
    if (!diagnosisFocus || diagnosisFocus.source === "map") return;
    if (diagnosisFocus === handledFocusRef.current) return;
    handledFocusRef.current = diagnosisFocus;

    const missionLogId = diagnosisFocus.missionLogId;
    if (missionLogId == null) return;
    lockMissionSelection();

    // "YYYY-MM-DD" 를 그 날 자정(로컬)으로 읽는다. new Date(문자열) 은 UTC 로 읽혀
    // 한국 시간대에서 하루 밀린다.
    const parts = String(diagnosisFocus?.date ?? "").split("-").map(Number);
    const nextDate =
      parts.length === 3 && parts.every(Number.isFinite)
        ? new Date(parts[0], parts[1] - 1, parts[2])
        : null;

    pendingFocusIdRef.current = missionLogId;
    if (nextDate) {
      setSelectedDate((prev) =>
        formatDateKey(prev) === formatDateKey(nextDate) ? prev : nextDate
      );
    }
    setSelectedMissionLogId(missionLogId);
    setSelectedMissionTime(diagnosisFocus?.time ?? "");
    setHasAppliedDate(true);
    setIsTimeListOpen(false);

    if (isDemo) {
      pendingFocusIdRef.current = null;
      return undefined;
    }

    // 그 날짜의 회차 목록도 같이 받아 둔다. 목록을 안 받으면 지도 드롭다운에
    // 이전 날짜의 회차가 남는다. 날짜는 상태 반영을 기다리지 않고 직접 넘긴다.
    let cancelled = false;
    (async () => {
      try {
        await fetchMissionLogForDateRef.current({
          selectId: missionLogId,
          date: nextDate ?? undefined,
          keepPreferred: true,
          isStale: () => cancelled,
        });
      } catch (error) {
        console.error("Failed to load mission logs for the selected run:", error);
      } finally {
        // 뒤이어 다른 회차를 골랐으면 그쪽이 이미 자기 값을 넣어 뒀다. 그때만 남겨 둔다.
        // 조건 없이 두면 취소된 요청에서 못 비워 잠금이 영영 풀리지 않는다.
        if (pendingFocusIdRef.current === missionLogId) {
          pendingFocusIdRef.current = null;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [diagnosisFocus, isDemo, lockMissionSelection]);

  // 진단 버튼을 껐다 켜면 지도는 직전에 보던 회차를 그대로 다시 그린다.
  // 이때는 목록을 다시 받지 않아 위의 알림 자리를 지나지 않으므로 여기서 채운다.
  // 포커스가 비어 있을 때만 말한다 — 회차를 바꾸는 중(포커스가 이미 차 있음)에
  // 끼어들면 아직 반영 안 된 옛 회차를 되돌려 보내게 된다.
  useEffect(() => {
    if (!toggle || selectedMissionLogId == null || diagnosisFocus) return;
    announceFocus(selectedMissionLogId, selectedDate, selectedMissionTime);
  }, [
    toggle,
    selectedMissionLogId,
    selectedMissionTime,
    selectedDate,
    diagnosisFocus,
    announceFocus,
  ]);

  // ── 칸 진단 컨택트시트 ───────────────────────────────────────
  // 크롭 한 장의 등급. 지도 칸을 칠할 때 쓰는 기준과 같은 값을 쓴다.
  // 병명이 없거나 NORMAL 이면 정상, 확률이 심각 기준 이상이면 심각, 주의 기준 이상이면 주의, 그 아래는 정상.
  // 예전엔 주의 기준 검사를 객체 단위 크롭에만 걸었다. "AI 가 주의 기준 미만은 안 보낸다" 는 전제였는데,
  // 주의 기준을 30→51 로 올리면서(2026-09-17) 그 사이 값이 사진 단위 결과로 들어와 전부 주의로 남았다.
  const segGrade = useCallback(
    (seg) => {
      if (!seg || !seg.name || seg.category === "NORMAL") return "ok";
      if (seg.conf != null && seg.conf >= gradeThresholds.high) return "high";
      if (seg.conf != null && seg.conf < gradeThresholds.mid) return "ok";
      return "mid";
    },
    [gradeThresholds.high, gradeThresholds.mid]
  );

  const cellSegs = useMemo(
    () =>
      cellPreviewList.map((item, idx) => ({
        item,
        idx,
        grade: segGrade(item),
      })),
    [cellPreviewList, segGrade]
  );

  // 병명·확률이 아예 안 오는 응답(데모, 합성 사진 한 장)에서는 칩을 감춘다.
  const hasSegmentGrades = useMemo(
    () => cellSegs.some((s) => s.item?.name),
    [cellSegs]
  );

  const cellCounts = useMemo(
    () =>
      cellSegs.reduce(
        (acc, s) => {
          acc[s.grade] += 1;
          return acc;
        },
        { high: 0, mid: 0, ok: 0 }
      ),
    [cellSegs]
  );

  const visibleSegs = useMemo(
    () =>
      cellFilter === "all"
        ? cellSegs
        : cellSegs.filter((s) => s.grade === cellFilter),
    [cellSegs, cellFilter]
  );

  const cellWorstGrade = cellCounts.high
    ? "high"
    : cellCounts.mid
      ? "mid"
      : "ok";

  // 요약 줄에 적을 병명. 가장 나쁜 장의 병명을 쓴다.
  const cellMainDisease = useMemo(() => {
    const worst = cellSegs
      .filter((s) => s.grade !== "ok" && s.item?.name)
      .sort((a, b) => (b.item.conf ?? 0) - (a.item.conf ?? 0))[0];
    return worst?.item?.name ?? "";
  }, [cellSegs]);

  // 크롭별 진단이 없으면 칸 단위 상태(diagnosis.label/summary/tone)로 적는다.
  const cellFallbackGrade = TONE_TO_GRADE[cellStatusTone] ?? "ok";
  const summaryGrade = hasSegmentGrades ? cellWorstGrade : cellFallbackGrade;
  const summaryDx = hasSegmentGrades ? cellMainDisease : cellStatusLabel;

  const zoomSeg = cellSegs[cellPreviewIndex] ?? null;

  // 카운터는 걸어 둔 필터 기준으로 센다. 심각만 보는 중이면 "심각 2 / 3장".
  const zoomPosText = useMemo(() => {
    if (!zoomSeg) return "";
    const at = visibleSegs.findIndex((s) => s.idx === zoomSeg.idx);
    const scope =
      cellFilter === "all" ? "" : `${t(`map.grade.${cellFilter}`)} `;
    return t("map.preview.zoomPos", {
      scope,
      at: at < 0 ? 1 : at + 1,
      total: visibleSegs.length,
    });
  }, [zoomSeg, visibleSegs, cellFilter, t]);

  // 날짜만 적는다. 회차 시각은 지도 위 날짜 선택기에 이미 나오고, 여기 머리글에서는
  // 칸 번호 옆에 초 단위까지 붙어 오히려 읽기를 방해했다.
  const previewWhenText = useMemo(() => {
    const d = selectedDate;
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
    return `${d.getMonth() + 1}/${d.getDate()}`;
  }, [selectedDate]);

  // 화살표는 걸어 둔 필터 안에서만 움직인다. 심각 칩을 켠 채 넘기면 빨간 장만 돈다.
  const stepCellPreview = useCallback(
    (dir) => {
      if (!visibleSegs.length) return;
      const at = visibleSegs.findIndex((s) => s.idx === cellPreviewIndex);
      const base = at < 0 ? 0 : at;
      const next =
        visibleSegs[(base + dir + visibleSegs.length) % visibleSegs.length];
      setCellPreviewIndex(next.idx);
    },
    [visibleSegs, cellPreviewIndex]
  );

  // 칩을 바꿨을 때 보고 있던 장이 그 등급에 없으면 첫 장으로 옮긴다.
  // 그대로 두면 크게 띄운 사진과 아래 목록이 서로 다른 것을 가리킨다.
  const selectCellFilter = useCallback(
    (key) => {
      setCellFilter(key);
      if (!isCellZoomOpen) return;
      const next =
        key === "all" ? cellSegs : cellSegs.filter((s) => s.grade === key);
      if (!next.length) {
        setIsCellZoomOpen(false);
        return;
      }
      if (!next.some((s) => s.idx === cellPreviewIndex)) {
        setCellPreviewIndex(next[0].idx);
      }
    },
    [cellSegs, cellPreviewIndex, isCellZoomOpen]
  );

  const handleSelectMissionTime = (item) => {
    if (!item?.id) return;
    lockMissionSelection();
    setSelectedMissionLogId(item.id);
    setSelectedMissionTime(item.time);
    setIsTimeListOpen(false);
    announceFocus(item.id, selectedDate, item.time);
    // 여기서 곧바로 부르면 방금 고른 미션이 아직 반영되기 전이라 이전 미션을 조회한다.
    // selectedMissionLogId 가 바뀌면 아래 useEffect 가 알아서 다시 부른다.
  };

  const handleToggleTimeList = () => {
    setIsTimeListOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!toggle) setIsTimeListOpen(false);
  }, [toggle]);

  useEffect(() => {
    setIsTimeListOpen(false);
    // 데이터 탭에서 다른 날짜의 회차를 고른 중이면 손대지 않는다. 여기서 지우면
    // 사용자가 누른 회차 대신 그날 마지막 회차가 잡힌다. 목록을 받아 오는 쪽에서
    // 회차를 다시 맞추고 이 자리를 비운다.
    if (pendingFocusIdRef.current != null) return;
    setHasAppliedDate(false);
    // 데모 목록은 위의 demoLogItems 효과가 날짜에 맞춰 다시 채우므로 비우지 않는다.
    // (미션 완료 시 selectedDate 가 같이 바뀌어 방금 채운 목록이 지워지던 문제)
    if (isDemo) return;
    setSelectedMissionLogId(null);
    setSelectedMissionTime("");
    setMissionTimeList([]);
  }, [selectedDate, isDemo]);

  useEffect(() => {
    if (!toggle) setHasAppliedDate(false);
  }, [toggle]);

  useEffect(() => {
    setHasAppliedDate(false);
    if (isDemo) return;
    setSelectedMissionLogId(null);
    setSelectedMissionTime("");
    setMissionTimeList([]);
  }, [farmId, isDemo]);

  const polygonRef = useRef(null);
  const didFitRef = useRef(false);
  const farmBoundsRef = useRef(null);
  const idleListenerRef = useRef(null);

  // [임시 비활성화] minZoom 잠금 기능과 함께 보류 (재활성화 시 idle 콜백 중복 방지용)
  // const registerIdleOnce = useCallback((map, handler) => {
  //   if (idleListenerRef.current) {
  //     window.google.maps.event.removeListener(idleListenerRef.current);
  //   }
  //   idleListenerRef.current = window.google.maps.event.addListenerOnce(
  //     map,
  //     "idle",
  //     () => {
  //       idleListenerRef.current = null;
  //       handler();
  //     }
  //   );
  // }, []);

  const fitToFarmOverview = useCallback(() => {
    const map = mapRef.current;
    const bounds = farmBoundsRef.current;
    if (!map || !bounds) return;

    // 연타로 지도가 이동 중일 때 구글맵 내부 줌 충돌(minZoom cannot exceed maxZoom)이 날 수 있어 방어
    try {
      map.setOptions({
        minZoom: 0,
        maxZoom: SELECTABLE_MAX_ZOOM,
        draggable: true,
        gestureHandling: "greedy",
        scrollwheel: true,
        disableDoubleClickZoom: false,
        keyboardShortcuts: true,
      });

      const mapWidth = mapElementRef.current?.offsetWidth || 0;
      const padding = Math.max(24, Math.round(mapWidth * 0.06));
      map.fitBounds(bounds, {
        top: padding,
        right: padding,
        bottom: padding,
        left: padding,
      });
    } catch (e) {
      // 연타 중 일시적 충돌 — 무시 (다음 정상 전환에서 복구됨)
    }
    // [임시 비활성화] minZoom 잠금: 먼 지역 농장 전환 시 'minZoom cannot exceed maxZoom' 에러로 보류
    // registerIdleOnce(map, () => {
    //   const z = map.getZoom();
    //   if (Number.isFinite(z)) {
    //     const safeMin = Math.min(z, SELECTABLE_MAX_ZOOM);
    //     map.setOptions({ minZoom: 0 });
    //     map.setOptions({ maxZoom: SELECTABLE_MAX_ZOOM });
    //     map.setOptions({ minZoom: safeMin });
    //   }
    // });
  }, [mapRef]);

  useEffect(() => {
    const map = mapRef.current;
    const path = toPath(boundary);
    farmPathRef.current = path;
    if (!map) return;

    if (path.length < 3) {
      farmBoundsRef.current = null;
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }
      return;
    }

    if (!polygonRef.current) {
      polygonRef.current = new window.google.maps.Polygon({
        paths: path,
        map,
        ...FARM_POLYGON_STYLE,
      });
    } else {
      polygonRef.current.setPaths(path);
      polygonRef.current.setOptions(FARM_POLYGON_STYLE);
      if (!polygonRef.current.getMap()) polygonRef.current.setMap(map);
    }

    const bounds = new window.google.maps.LatLngBounds();
    path.forEach((p) => bounds.extend(p));
    farmBoundsRef.current = bounds;

    if (LOCK_MAP_INTERACTION) {
      // 연타로 지도가 이동 중일 때 구글맵 내부 줌 충돌(minZoom cannot exceed maxZoom)이 날 수 있어 방어
      try {
        map.setOptions({
          minZoom: 0,
          maxZoom: SELECTABLE_MAX_ZOOM,
          draggable: true,
          gestureHandling: "greedy",
          scrollwheel: true,
          disableDoubleClickZoom: false,
          keyboardShortcuts: true,
        });
        const mapWidth = mapElementRef.current?.offsetWidth || 0;
        const padding = Math.max(
          24,
          Math.round(mapWidth * 0.06)
        );
        map.fitBounds(bounds, {
          top: padding,
          right: padding,
          bottom: padding,
          left: padding,
        });
        setIsFarmFitted(true);
      } catch (e) {
        // 연타 중 일시적 충돌 — 무시 (다음 정상 전환에서 복구됨)
      }
      // [임시 비활성화] minZoom 잠금: 먼 지역 농장 전환 시 'minZoom cannot exceed maxZoom' 에러로 보류
      // registerIdleOnce(map, () => {
      //   const z = map.getZoom();
      //   if (Number.isFinite(z)) {
      //     const safeMin = Math.min(z, SELECTABLE_MAX_ZOOM);
      //     map.setOptions({ minZoom: 0 });
      //     map.setOptions({ maxZoom: SELECTABLE_MAX_ZOOM });
      //     map.setOptions({ minZoom: safeMin });
      //   }
      // });
    } else if (!didFitRef.current) {
      map.fitBounds(bounds);
      didFitRef.current = true;
      setIsFarmFitted(true);
    }

    return () => {
      if (idleListenerRef.current) {
        window.google.maps.event.removeListener(idleListenerRef.current);
        idleListenerRef.current = null;
      }
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }
    };
  }, [boundary, mapRef, applyLockedZoom, toggle, isMapReady]);

  useEffect(() => {
    didFitRef.current = false;
  }, [farmId]);

  // 지도 좌표 → 컨테이너 픽셀 변환용 오버레이. 그리는 것 없이 projection 만 가져다 쓴다.
  useEffect(() => {
    if (!isMapReady || !mapRef.current || !window.google?.maps) return;
    const overlay = new window.google.maps.OverlayView();
    overlay.onAdd = () => {};
    overlay.draw = () => {};
    overlay.onRemove = () => {};
    overlay.setMap(mapRef.current);
    projectionOverlayRef.current = overlay;
    return () => {
      overlay.setMap(null);
      projectionOverlayRef.current = null;
    };
  }, [isMapReady, mapRef]);

  // 칸 위에 마우스를 올렸을 때 뜨는 요약.
  // 등급과 장 수가 cell-status 응답에 이미 담겨 오므로 서버를 다시 부르지 않는다.
  const cancelGradeTipHide = useCallback(() => {
    if (gradeTipHideTimerRef.current) {
      clearTimeout(gradeTipHideTimerRef.current);
      gradeTipHideTimerRef.current = null;
    }
  }, []);

  const closeGradeTip = useCallback(() => {
    cancelGradeTipHide();
    setGradeTip(null);
  }, [cancelGradeTipHide]);

  const showGradeTip = useCallback((cellId, summary, center) => {
    cancelGradeTipHide();
    const projection = projectionOverlayRef.current?.getProjection();
    if (!projection || !window.google?.maps) return;
    const point = projection.fromLatLngToContainerPixel(
      new window.google.maps.LatLng(center.latitude, center.longitude)
    );
    if (!point) return;
    setGradeTip({ cellId, summary, x: point.x, y: point.y });
  }, [cancelGradeTipHide]);

  // 칸에서 마우스가 빠져도 잠깐은 남겨 둔다.
  // 그 사이에 커서가 툴팁 안으로 들어오면 예약을 취소하고 그대로 남는다.
  const hideGradeTip = useCallback(() => {
    cancelGradeTipHide();
    gradeTipHideTimerRef.current = setTimeout(() => {
      gradeTipHideTimerRef.current = null;
      setGradeTip(null);
    }, 180);
  }, [cancelGradeTipHide]);

  // 분석 모드를 끄면 남아 있던 툴팁도 같이 사라져야 한다.
  useEffect(() => {
    if (!toggle) closeGradeTip();
  }, [toggle, closeGradeTip]);

  useEffect(() => cancelGradeTipHide, [cancelGradeTipHide]);

  // 안전장치: boundary 가 없는 농장(또는 map-overlay 실패) 이면 fitBounds 가 영영
  // 실행되지 않으므로, 지도 준비 후 일정 시간이 지나면 커버를 강제로 걷는다.
  useEffect(() => {
    if (!isMapReady || isFarmFitted) return;
    const timerId = setTimeout(() => setIsFarmFitted(true), 4000);
    return () => clearTimeout(timerId);
  }, [isMapReady, isFarmFitted]);

  useEffect(() => {
    if (!polygonRef.current) return;
    polygonRef.current.setOptions(
      toggle ? FARM_POLYGON_STYLE_ANALYSIS : FARM_POLYGON_STYLE
    );
  }, [toggle]);

  useEffect(() => {
    if (!toggle) {
      setIsCellImageOpen(false);
      setIsCellPreviewOpen(false);
      setIsGradCam(false);
      setCellPreviewList([]);
      setCellPreviewIndex(0);
      setCellStatusLabel("");
      setCellStatusNote("");
      setCellStatusTone("warning");
      setCellCoordText("");
    }
  }, [toggle]);

  useEffect(() => {
    if (
      cellPreviewList.length > 0 &&
      cellPreviewIndex > cellPreviewList.length - 1
    ) {
      setCellPreviewIndex(0);
    }
  }, [cellPreviewList, cellPreviewIndex]);

  useEffect(() => {
    if (!mapRef.current || !isMapReady) return;

    gridPolygonsRef.current.forEach((poly) => poly.setMap(null));
    gridPolygonsRef.current = [];
    gridLinesRef.current.forEach((line) => line.setMap(null));
    gridLinesRef.current = [];
    gridPolygonByIdRef.current = new Map();

    if (!Array.isArray(gridCells) || gridCells.length === 0) return;

    // const farmPath = farmPathRef.current;
    // const hasFarmPath = Array.isArray(farmPath) && farmPath.length >= 3;
    const selectedIds = new Set(
      toggle ? analysisCellIds || [] : selectedCellIds || []
    );

    const dashSymbol = {
      path: "M 0,-1 0,1",
      strokeOpacity: 1,
      scale: 1,
    };

    gridCells.forEach((cell) => {
      const vertices = cell.vertices || [];
      if (vertices.length < 3) return;

      const center = cell.center;
      // if (hasFarmPath && center) {
      //   const isInside = isPointInside(
      //     { lat: center.latitude, lng: center.longitude },
      //     farmPath
      //   );
      //   if (!isInside) return;
      // }

      const paths = vertices.map((v) => ({
        lat: v.latitude,
        lng: v.longitude,
      }));
      const expandScale = 1.12;
      const expandedPaths = center
        ? paths.map((p) => ({
            lat: center.latitude + (p.lat - center.latitude) * expandScale,
            lng: center.longitude + (p.lng - center.longitude) * expandScale,
          }))
        : paths;

      const isSelected = selectedIds.has(cell.id);
      const summary = toggle ? cellGradeMap[cell.id] : null;
      const hasGradeData = toggle && Object.keys(cellGradeMap).length > 0;
      // 등급을 받은 뒤로는 "진단이 있는 칸"이면 모두 누를 수 있다.
      // 예전에는 빨간 칸만 눌렸는데, 초록 칸도 열어봐야 정말 멀쩡한지 확인이 된다.
      const isAnalysisSelectable = !toggle
        ? true
        : hasGradeData
          ? Boolean(summary)
          : isAnalysisAvailable && (!analysisCellIds.length || isSelected);
      const gradeStyle = summary ? gradeCellStyle(summary) : null;
      // 등급 색이 있으면 선택 강조가 그 위를 덮지 않게 테두리만 밝힌다.
      const selectedStyle = !isSelected
        ? null
        : gradeStyle
          ? { strokeColor: "#B8FFE9", strokeOpacity: 1, strokeWeight: 2, zIndex: 4 }
          : toggle
            ? GRID_CELL_SELECTED_STYLE_ANALYSIS
            : GRID_CELL_SELECTED_STYLE;
      const baseCellStyle = {
        ...GRID_CELL_STYLE,
        ...(gradeStyle || null),
        clickable: isAnalysisSelectable,
        cursor: isAnalysisSelectable ? "pointer" : "default",
        ...(selectedStyle || null),
      };
      const polygon = new window.google.maps.Polygon({
        paths: expandedPaths,
        map: mapRef.current,
        ...baseCellStyle,
      });

      const handleCellSelect = () => {
        if (toggle) {
          // 등급을 받은 뒤로는 진단이 있는 칸이면 모두 연다.
          if (hasGradeData ? !summary : analysisCellIds.length && !selectedIds.has(cell.id)) return;
          openCellImage(cell);
          if (onCellClick) onCellClick(cell);
          return;
        }
        if (onCellClick) onCellClick(cell);
      };

      polygon.addListener("click", handleCellSelect);
      polygon.addListener("mouseover", () => {
        if (!toggle) dispatch(setHoverCellId(cell.id));
        if (summary && center) showGradeTip(cell.id, summary, center);
        if (!isAnalysisSelectable) return;
        polygon.setOptions(
          isSelected
            ? { strokeColor: "#B8FFE9", strokeOpacity: 1, strokeWeight: 2, zIndex: 4 }
            : gradeStyle
              ? { ...gradeStyle, strokeColor: "#B8FFE9", strokeOpacity: 1, strokeWeight: 2, zIndex: 4 }
              : GRID_CELL_HOVER_STYLE
        );
      });
      polygon.addListener("mouseout", () => {
        if (!toggle) dispatch(setHoverCellId(null));
        hideGradeTip();
        polygon.setOptions(baseCellStyle);
      });

      gridPolygonsRef.current.push(polygon);
      gridPolygonByIdRef.current.set(cell.id, { polygon, base: baseCellStyle });

      const line = new window.google.maps.Polyline({
        map: mapRef.current,
        path: [...expandedPaths, expandedPaths[0]],
        strokeOpacity: 0,
        strokeWeight: 1,
        strokeColor: "#0C1620",
        clickable: false,
        zIndex: 1,
        icons: [
          {
            icon: dashSymbol,
            offset: "0",
            repeat: "4px",
          },
        ],
      });

      gridLinesRef.current.push(line);
    });

    return () => {
      gridPolygonsRef.current.forEach((poly) => poly.setMap(null));
      gridPolygonsRef.current = [];
      gridLinesRef.current.forEach((line) => line.setMap(null));
      gridLinesRef.current = [];
    };
  }, [
    gridCells,
    selectedCellIds,
    analysisCellIds,
    cellGradeMap,
    showGradeTip,
    hideGradeTip,
    mapRef,
    isMapReady,
    onCellClick,
    toggle,
    openCellImage,
    dispatch,
  ]);

  // 비행 순서. 업로드와 같은 함수라 선이 곧 드론이 도는 길이다.
  const flightOrderIds = useMemo(
    () => (toggle ? [] : flightOrder(selectedCellIds || [], gridCells)),
    [toggle, selectedCellIds, gridCells]
  );

  // [경로 선] 비행 전 계획 경로. 드론 경로 선(useMarkers 의 민트 점선 + 검은 그림자)과 같은 점선·그림자에
  // 색만 흰색으로 그리고, 방향 화살표와 출발 점을 더한다. 비행이 시작되면 드론 경로 선이 같은 경로를
  // 민트로 이어받으므로 이 선은 숨긴다. 분석 모드에서는 그리지 않는다.
  useEffect(() => {
    const clear = () => {
      (flightPathRef.current || []).forEach((line) => line.setMap(null));
      flightPathRef.current = null;
    };
    clear();
    if (!mapRef.current || !isMapReady || !window.google?.maps) return undefined;
    if (missionFlying || flightOrderIds.length < 2) return undefined;

    const cellMap = new Map((gridCells || []).map((cell) => [cell.id, cell]));
    const path = flightOrderIds
      .map((id) => cellMap.get(id)?.center)
      .filter(Boolean)
      .map((c) => ({ lat: Number(c.latitude), lng: Number(c.longitude) }));
    if (path.length < 2) return undefined;

    const { SymbolPath, Polyline } = window.google.maps;
    const PLAN_WHITE = "#FFFFFF";
    const dash = { path: "M 0,-2 0,2", strokeColor: PLAN_WHITE, strokeOpacity: 1, strokeWeight: 3, scale: 1 };
    // 드론 경로 선과 같은 그림자: 살짝 어긋난 위치에 검은 점선 (useMarkers shadowConfig 와 같은 값)
    const shadow = new Polyline({
      map: mapRef.current,
      path: path.map((pt) => ({ lat: pt.lat - 0.00001, lng: pt.lng + 0.00001 })),
      clickable: false,
      zIndex: 5,
      strokeOpacity: 0,
      strokeWeight: 0,
      icons: [{ icon: { ...dash, strokeColor: "#000000", strokeOpacity: 0.3, strokeWeight: 7 }, offset: "0", repeat: "14px" }],
    });
    const line = new Polyline({
      map: mapRef.current,
      path,
      clickable: false,
      zIndex: 6,
      strokeOpacity: 0,
      strokeWeight: 0,
      icons: [
        { icon: dash, offset: "0", repeat: "14px" },
        {
          icon: { path: SymbolPath.FORWARD_CLOSED_ARROW, scale: 2.8, fillColor: PLAN_WHITE, fillOpacity: 1, strokeColor: "#06121A", strokeWeight: 1 },
          offset: "40px",
          repeat: "72px",
        },
        {
          icon: { path: SymbolPath.CIRCLE, scale: 6, fillColor: PLAN_WHITE, fillOpacity: 1, strokeColor: "#06121A", strokeWeight: 2 },
          offset: "0%",
        },
      ],
    });
    flightPathRef.current = [shadow, line];

    return clear;
  }, [flightOrderIds, gridCells, mapRef, isMapReady, missionFlying]);

  // [선택 포커스] 선택이 비어 있다가 처음 생기면, 마지막 클릭 뒤 잠깐 기다렸다가 고른 셀이 사이드바에
  // 가리지 않고 화면 가운데 크게 오도록 한 번 맞춘다. 이후 셀을 더 고르거나 빼도 지도는 그대로 둔다
  // (선택마다 움직이면 버벅여 보였다). 선택이 비면(해제·미션 종료 시 서버가 비움) 농장 전체 보기로 되돌린다.
  // 분석 모드에서는 움직이지 않는다.
  // 데스크톱은 오른쪽 플로팅 사이드바(430px + 여백), 모바일은 아래 시트가 지도를 덮는다.
  const focusPadding = useCallback(() => {
    const w = mapElementRef.current?.offsetWidth || 0;
    const h = mapElementRef.current?.offsetHeight || 0;
    const pad = Math.max(40, Math.round(Math.min(w, h) * 0.12));
    const padding =
      w <= 900
        ? { top: 90, left: pad, right: pad, bottom: Math.round(h * 0.45) }
        : { top: 110, left: pad, bottom: pad, right: 430 + 26 + pad };
    if (w - padding.left - padding.right < 160 || h - padding.top - padding.bottom < 160) {
      return { top: pad, left: pad, right: pad, bottom: pad };
    }
    return padding;
  }, []);

  const fitToCells = useCallback(
    (ids) => {
      const map = mapRef.current;
      if (!map || !window.google?.maps) return;
      const cellMap = new Map((gridCells || []).map((cell) => [cell.id, cell]));
      const bounds = new window.google.maps.LatLngBounds();
      let points = 0;
      ids.forEach((id) => {
        const cell = cellMap.get(id);
        const vs = cell?.vertices?.length ? cell.vertices : cell?.center ? [cell.center] : [];
        vs.forEach((v) => {
          bounds.extend({ lat: Number(v.latitude), lng: Number(v.longitude) });
          points += 1;
        });
      });
      if (points === 0) return;

      try {
        map.fitBounds(bounds, focusPadding());
      } catch (e) {
        // 이동 중 연타로 인한 일시적 줌 충돌 — 무시
      }
    },
    [gridCells, mapRef, focusPadding]
  );

  const selectionKey = useMemo(
    () => [...(selectedCellIds || [])].sort((a, b) => a - b).join(","),
    [selectedCellIds]
  );
  // 지금 선택으로 이미 한 번 맞췄는지. 선택이 비면 풀려서 다음 선택 때 다시 한 번 맞춘다.
  const focusedRef = useRef(false);
  useEffect(() => {
    if (!isMapReady || toggle || !Array.isArray(gridCells) || gridCells.length === 0) return undefined;
    const ids = selectedCellIds || [];
    if (ids.length > 0 && focusedRef.current) return undefined;
    const timer = setTimeout(() => {
      if (ids.length === 0) {
        if (focusedRef.current) {
          focusedRef.current = false;
          fitToFarmOverview();
        }
        return;
      }
      focusedRef.current = true;
      fitToCells(ids);
    }, 900);
    return () => clearTimeout(timer);
    // selectedCellIds 는 selectionKey 로 대신 본다(같은 셀이면 배열이 새로 와도 다시 계산하지 않는다)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey, isMapReady, toggle, gridCells, fitToCells, fitToFarmOverview]);

  // [회차 포커스] 작물 분석 탭(분석 모드)에서 회차를 고르면 그 회차에 찍힌 셀이 화면 가운데 크게 오도록
  // 한 번 맞춘다. 셀 선택 포커스와 같은 여백·같은 방식이다. 다른 회차를 고르면 다시 한 번 맞추고,
  // 분석 모드를 끄면 농장 전체 보기로 되돌린다.
  const runCellIds = useMemo(() => {
    const graded = Object.keys(cellGradeMap || {}).map(Number).filter(Number.isFinite);
    return graded.length ? graded : analysisCellIds || [];
  }, [cellGradeMap, analysisCellIds]);
  const runFocusKeyRef = useRef("");
  useEffect(() => {
    if (!isMapReady) return undefined;
    if (!toggle) {
      if (runFocusKeyRef.current) {
        runFocusKeyRef.current = "";
        fitToFarmOverview();
      }
      return undefined;
    }
    if (selectedMissionLogId == null || runCellIds.length === 0) return undefined;
    const key = String(selectedMissionLogId);
    if (runFocusKeyRef.current === key) return undefined;
    // 등급과 셀 목록이 연달아 들어오므로 잠깐 기다렸다가 한 번만 움직인다.
    const timer = setTimeout(() => {
      runFocusKeyRef.current = key;
      fitToCells(runCellIds);
    }, 300);
    return () => clearTimeout(timer);
  }, [toggle, selectedMissionLogId, runCellIds, isMapReady, fitToCells, fitToFarmOverview]);

  // [호버 연동] 미션 박스 칩이나 지도 셀에 올린 셀을 지도에서 강조하고 순번 툴팁을 띄운다.
  useEffect(() => {
    const entry = hoverCellId != null ? gridPolygonByIdRef.current.get(hoverCellId) : null;
    if (entry) {
      entry.polygon.setOptions({ strokeColor: "#B8FFE9", strokeOpacity: 1, strokeWeight: 2.5, zIndex: 5 });
    }
    const idx = hoverCellId != null ? flightOrderIds.indexOf(hoverCellId) : -1;
    const cell = idx >= 0 ? (gridCells || []).find((c) => c.id === hoverCellId) : null;
    const projection = projectionOverlayRef.current?.getProjection();
    if (cell?.center && projection && window.google?.maps) {
      const point = projection.fromLatLngToContainerPixel(
        new window.google.maps.LatLng(Number(cell.center.latitude), Number(cell.center.longitude))
      );
      setOrderTip(
        point
          ? {
              x: point.x,
              y: point.y,
              text: t("map.orderTip.text", { at: idx + 1, total: flightOrderIds.length }),
            }
          : null
      );
    } else {
      setOrderTip(null);
    }
    return () => {
      if (entry) entry.polygon.setOptions(entry.base);
    };
  }, [hoverCellId, flightOrderIds, gridCells, t]);


  const onMapClickRef = useRef(onMapClick);

  useEffect(() => {
    onMapClickRef.current = onMapClick;
  }, [onMapClick]);

  useEffect(() => {
    if (mapRef.current) {
      setIsMapReady(true);
      return;
    }

    const initMap = async () => {
      try {
        const waitForMapContainer = () =>
          new Promise((resolve, reject) => {
            let attempts = 0;
            const maxAttempts = 120;
            const check = () => {
              const el = mapElementRef.current;
              if (el && el.offsetWidth > 0 && el.offsetHeight > 0) {
                resolve(el);
                return;
              }
              attempts += 1;
              if (attempts >= maxAttempts) {
                reject(new Error("Map container not ready"));
                return;
              }
              requestAnimationFrame(check);
            };
            check();
          });

        const loader = new Loader({
          apiKey: GOOGLE_MAPS_API_KEY,
          version: "weekly",
          libraries: ["marker"],
        });

        await loader.load();

        const { Map } = await window.google.maps.importLibrary("maps");
        const { AdvancedMarkerElement } =
          await window.google.maps.importLibrary("marker");

        if (AdvancedMarkerElement) {
          if (!window.google.maps.marker) window.google.maps.marker = {};
          window.google.maps.marker.AdvancedMarkerElement =
            AdvancedMarkerElement;
          // console.log("AdvancedMarkerElement loaded successfully");
        } else {
          console.error("Failed to load AdvancedMarkerElement");
        }

        let initOptions = {
          center: defaultCenter,
          zoom: defaultZoom,
          mapTypeId: "satellite",
          mapId: "YOUR_MAP_ID",
          disableDefaultUI: true,
        };

        if (LOCK_MAP_INTERACTION) {
          const responsiveZoom = getResponsiveZoom(
            mapElementRef.current?.offsetWidth
          );
          initOptions = {
            ...initOptions,
            zoom: responsiveZoom,
            draggable: true,
            gestureHandling: "greedy",
            scrollwheel: true,
            disableDoubleClickZoom: false,
            keyboardShortcuts: true,
          };
        }

        const path = toPath(boundary);

        if (path.length >= 3) {
          const tmpBounds = new window.google.maps.LatLngBounds();
          path.forEach((p) => tmpBounds.extend(p));
          const centerLL = tmpBounds.getCenter();
          initOptions = {
            ...initOptions,
            center: { lat: centerLL.lat(), lng: centerLL.lng() },
          };
        }

        const container = await waitForMapContainer();
        const map = new Map(container, initOptions);

        if (LOCK_MAP_INTERACTION && path.length < 3) {
          applyLockedZoom(map);
        }

        mapRef.current = map;
        setIsMapReady(true);
        // 생성 시점에 이미 boundary 가 있으면 지도 자체가 농장 중심으로 만들어지므로 바로 공개
        if (path.length >= 3) setIsFarmFitted(true);

        // // // console.log("Google Maps 초기화 완료");

        if (path.length >= 3 && !didFitRef.current && !LOCK_MAP_INTERACTION) {
          const bounds = new window.google.maps.LatLngBounds();
          path.forEach((p) => bounds.extend(p));
          map.fitBounds(bounds);
          didFitRef.current = true;
        }
      } catch (error) {
        console.error("Google Maps 초기화 실패:", error);
      }
    };

    initMap();
  }, []); // 빈 의존성 배열로 한 번만 실행

  useEffect(() => {
    if (!mapRef.current || !isMapReady) return;

    const listener = window.google.maps.event.addListener(
      mapRef.current,
      "click",
      (e) => {
        if (!e.latLng) return;
        onMapClickRef.current?.({ lat: e.latLng.lat(), lng: e.latLng.lng() });
      }
    );

    return () => window.google.maps.event.removeListener(listener);
  }, [isMapReady]);


  return (
    <div className={toggle ? "drone-map is-analysis" : "drone-map"}>
      <div style={{ width: "100%", height: "100%", position: "relative" }}>
        <div
          ref={mapElementRef}
          className="drone-map-canvas"
          style={{ width: "100%", height: "100%" }}
        />
        <div
          className={`calm-map-cover${isFarmFitted ? " is-done" : ""}`}
          aria-hidden="true"
        >
          <span>{t("map.loading.map")}</span>
        </div>
        <button
          type="button"
          className="map-fit-button"
          onClick={fitToFarmOverview}
          aria-label={t("map.fit.aria")}
        >
          {t("map.fit.label")}
        </button>

        {orderTip && !gradeTip && (
          <div className="order-tip" style={{ left: orderTip.x, top: orderTip.y }} role="status">
            {orderTip.text}
          </div>
        )}

        {gradeTip && (
          <div
            className="grade-tip"
            style={{ left: gradeTip.x, top: gradeTip.y }}
            onMouseEnter={cancelGradeTipHide}
            onMouseLeave={closeGradeTip}
          >
            <button
              type="button"
              className="grade-tip-close"
              onClick={closeGradeTip}
              aria-label={t("map.gradeTip.close")}
            >
              X
            </button>
            <div className="grade-tip-title">
              {t("map.cell.title", { cellId: gradeTip.cellId })}
            </div>
            <div className="grade-tip-sub">
              {t("map.gradeTip.detected", {
                count: gradeTip.summary.detectedCount || 0,
                grade: GRADE_KEY[gradeTip.summary.grade]
                  ? t(`map.grade.${GRADE_KEY[gradeTip.summary.grade]}`)
                  : "-",
              })}
            </div>
            {[
              ["HIGH", gradeTip.summary.highCount || 0],
              ["MID", gradeTip.summary.midCount || 0],
              ["OK", gradeTip.summary.okCount || 0],
            ].map(([grade, count]) => (
              <div className="grade-tip-row" key={grade}>
                <span className="grade-tip-label">
                  <i style={{ background: GRADE_TIP_COLOR[grade] }} />
                  {t(`map.grade.${GRADE_KEY[grade]}`)}
                </span>
                <span
                  className={`grade-tip-value${count ? "" : " is-zero"}`}
                  style={count ? { color: GRADE_TIP_COLOR[grade] } : undefined}
                >
                  {count}
                </span>
              </div>
            ))}
          </div>
        )}

        {isMapReady && !toggle && <DroneTracker mapRef={mapRef} />}
        {isMapReady && <EnergyDeviceMarkers mapRef={mapRef} isMapReady={isMapReady} />}
        {/* 촬영 여부는 칸 요약(cells)으로 본다. 예전에 쓰던 analysisCellIds 는
            심각(빨간) 칸 목록이라, 다 정상인 회차가 "데이터 없음"으로 나왔다. */}
        {toggle && hasAppliedDate && isAnalysisAvailable &&
          Object.keys(cellGradeMap).length === 0 && (
          <div style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "rgba(0,0,0,0.6)",
            color: "#fff",
            padding: "12px 24px",
            borderRadius: "8px",
            fontSize: "14px",
            pointerEvents: "none",
            zIndex: 10,
          }}>
            {t("map.empty.noFlightData")}
          </div>
        )}
        {toggle && (
          <div
            className={`ndvi-datetime${
              isTimeListOpen ? " ndvi-datetime--open" : ""
            }`}
          >
            <div className="ndvi-datetime-row">
              <button
                type="button"
                className={`ndvi-datetime-icon${!hasMissionDates ? " is-disabled" : ""}`}
                onClick={() => {
                  if (!hasMissionDates) {
                    toast.info(t("map.toast.noAnalysis"));
                    return;
                  }
                  setIsDateOpen(true);
                }}
                aria-label={t("map.date.openPicker")}
              >
                <img src={calendarIcon} alt="" />
              </button>
              <span className="ndvi-datetime-text">
                {toggle
                  ? hasAppliedDate
                    ? `${
                        formatSelectedDate(selectedDate) || formatNow(now)
                      }${selectedMissionTime ? ` ${selectedMissionTime}` : ""}`
                    : formatSelectedDate(selectedDate) || formatNow(now)
                  : formatNow(now)}
              </span>
              <button
                type="button"
                className="ndvi-datetime-caret"
                aria-label={t("map.date.caret")}
                onClick={handleToggleTimeList}
              >
                {"\u25BE"}
              </button>
            </div>
            {isTimeListOpen && (
              <div className="ndvi-time-list">
                {missionTimeList.length > 0 ? (
                  missionTimeList.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="ndvi-time-item"
                      onClick={() => handleSelectMissionTime(item)}
                    >
                      {item.label}
                    </button>
                  ))
                ) : (
                  <div className="ndvi-time-empty">{t("map.date.noLogs")}</div>
                )}
              </div>
            )}
          </div>
        )}

        {toggle && (
          // 색이 네 가지로 늘었으니 무슨 뜻인지 화면에 남아 있어야 한다.
          // 규칙(확률 몇 %)과 할 일을 같이 적어야 색만 보고 헤매지 않는다.
          <div className="ndvi-legend">
            <div className="ndvi-legend-title">{t("map.legend.title")}</div>
            <div className="ndvi-legend-row">
              <i style={{ background: GRADE_FILL.HIGH }} />
              <b style={{ color: GRADE_FILL.HIGH }}>{t("map.grade.high")}</b>
              <span>{t("map.legend.highRange", { high: gradeThresholds.high })}</span>
            </div>
            <div className="ndvi-legend-row">
              <i style={{ background: GRADE_FILL.MID }} />
              <b style={{ color: GRADE_FILL.MID }}>{t("map.grade.mid")}</b>
              <span>
                {t("map.legend.midRange", {
                  mid: gradeThresholds.mid,
                  max: gradeThresholds.high - 1,
                })}
              </span>
            </div>
            <div className="ndvi-legend-row">
              <i style={{ background: GRADE_FILL.OK }} />
              <b style={{ color: GRADE_FILL.OK }}>{t("map.grade.ok")}</b>
              <span>{t("map.legend.okNote")}</span>
            </div>
            <div className="ndvi-legend-row">
              <i className="ndvi-legend-none" />
              <b>{t("map.legend.none")}</b>
              <span>{t("map.legend.noneNote")}</span>
            </div>
          </div>
        )}

        {/* 지도 도구: 진단 / 확대 / 축소 — 신규 UI */}
        {/* ＋/－ 는 모바일(≤900px)에서 감춘다(CalmTheme.css). 손가락으로 확대·축소하면 된다. */}
        <div className="calm-tools">
          {/* 예전엔 "N"(생육지수) 이었는데 무슨 버튼인지 묻는 사람이 많았다.
              하는 일이 병해 진단 결과 보기라 글자를 그대로 바꿨다.
              버튼 폭이 38px 고정이라 두 글자까지만 안 깨진다. */}
          <button
            type="button"
            className={`calm-tool${toggle ? " on" : ""}`}
            title={
              toggle ? t("map.tools.diagnosisOff") : t("map.tools.diagnosisOn")
            }
            aria-pressed={toggle}
            onClick={() => dispatch(setToggle(!toggle))}
          >
            {t("map.tools.diagnosis")}
          </button>
          <button
            type="button"
            className="calm-tool calm-tool--zoom"
            title={t("map.tools.zoomIn")}
            onClick={() => {
              const map = mapRef.current;
              if (map) map.setZoom((map.getZoom() ?? 15) + 1);
            }}
          >
            ＋
          </button>
          <button
            type="button"
            className="calm-tool calm-tool--zoom"
            title={t("map.tools.zoomOut")}
            onClick={() => {
              const map = mapRef.current;
              if (map) map.setZoom((map.getZoom() ?? 15) - 1);
            }}
          >
            －
          </button>
        </div>

        {/* 기존 NDVI 토글 스위치 — 신규 UI의 진단 버튼으로 대체. 삭제하지 않고 감춰둠.
        <div className="ndvi-container">
          <div className="ndvi-content">
            <NDVIToggle />
          </div>
        </div>
        */}
        {isCellImageOpen && (
          <div
            className="cell-image-overlay"
            onClick={() => {
              setIsCellImageOpen(false);
              setIsCellPreviewOpen(false);
            }}
          >
            <div
              className="cell-image-panel"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="cell-image-close"
                onClick={() => {
                  setIsCellImageOpen(false);
                  setIsCellPreviewOpen(false);
                }}
                aria-label={t("map.cellImage.close")}
              >
                X
              </button>
              {cellImageUrl && (
                <img
                  className="cell-image-main"
                  src={cellImageUrl}
                  alt={t("map.cellImage.alt")}
                />
              )}
              {!cellImageUrl && !isCellImageLoading && (
                <div className="cell-image-empty">{t("map.cellImage.empty")}</div>
              )}
            </div>
          </div>
        )}
        {isCellImageOpen && cellThumbUrl && (
          <button
            type="button"
            className="cell-image-thumb"
            onClick={() => {
              setCellPreviewIndex(0);
              setIsGradCam(false);
              setIsCellPreviewOpen(true);
            }}
            aria-label={t("map.cellImage.openPreview")}
          >
            <img src={cellThumbUrl} alt={t("map.cellImage.thumbAlt")} />
          </button>
        )}
        {/* ── 칸 진단: 컨택트시트 ──
            한 칸에 크롭이 수십 장 온다. 예전엔 한 장씩 화살표로 넘겨서, 35장이면
            34번을 눌러야 그 칸 사정을 알 수 있었다. 전부 격자로 깔고 등급 색만 입혀
            누르지 않고도 몇 장이 문제인지 보이게 한다. 한 장을 자세히 볼 때만 크게 연다. */}
        {isCellPreviewOpen && (
          <div
            className="cell-image-preview-overlay"
            onClick={() => setIsCellPreviewOpen(false)}
          >
            <div
              className="cell-image-preview-panel"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="cip-head">
                <span className="cip-cell">
                  {cellNo == null
                    ? t("map.preview.title")
                    : t("map.cell.title", { cellId: cellNo })}
                </span>
                <span className="cip-when">{previewWhenText}</span>
                <button
                  type="button"
                  className="cip-close"
                  onClick={() => setIsCellPreviewOpen(false)}
                  aria-label={t("map.preview.close")}
                >
                  {"✕"}
                </button>
              </div>

              {cellSegs.length === 0 ? (
                <div className="cip-empty">{t("map.preview.empty")}</div>
              ) : (
                <>
                  <div className="cip-sum">
                    <span className={`cip-grade cip-grade--${summaryGrade}`}>
                      <i />
                      {t(`map.grade.${summaryGrade}`)}
                    </span>
                    {summaryDx && <span className="cip-dx">{summaryDx}</span>}
                    <span className="cip-count">
                      {cellSegs.some((s) => s.item.cropObjectKey)
                        ? t("map.preview.objectsAndDiagnoses", {
                            count: cellSegs.length,
                            diagnoses: cellSegs.reduce((n, s) => n + (s.item.diagnoses?.length ?? 1), 0),
                          })
                        : t("map.preview.detected", { count: cellSegs.length })}
                    </span>
                    {/* Grad-CAM 은 예전에 확대 화면 안에만 있어 한 장씩 켜야 했고, 다음 장을 열면 꺼졌다.
                        여기로 올려 목록과 확대 화면 모두에 한 번에 적용한다. */}
                    <label className={`cip-gcam${isGradCam ? " is-on" : ""}`}>
                      <input
                        type="checkbox"
                        checked={isGradCam}
                        onChange={(e) => setIsGradCam(e.target.checked)}
                      />
                      <span className="cip-track" />
                      {t("map.preview.gradcam")}
                    </label>
                  </div>

                  {/* 등급 정보가 오는 회차에만 칩을 띄운다. 데모나 합성 사진 한 장짜리
                      응답에는 병명·확률이 없어서, 칩을 띄우면 전부 "정상"으로만 보인다. */}
                  {hasSegmentGrades && (
                    <div className="cip-chips">
                      {CELL_FILTERS.map((f) => {
                        const n =
                          f.key === "all" ? cellSegs.length : cellCounts[f.key];
                        return (
                          <button
                            key={f.key}
                            type="button"
                            className={`cip-chip${f.cls}${
                              cellFilter === f.key ? " is-on" : ""
                            }`}
                            disabled={n === 0}
                            aria-pressed={cellFilter === f.key}
                            onClick={() => selectCellFilter(f.key)}
                          >
                            {t(f.labelKey)}
                            <span className="n">{n}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* 높이 고정은 바깥 상자가 맡는다. 격자 자체에 높이를 주면 행이 그 높이에 맞춰
                      눌려 타일이 정사각형을 잃고 사진이 서로 붙어 보인다. */}
                  <div
                    className={`cip-gridwrap${isCellGridScrolling ? " is-scrolling" : ""}`}
                    onScroll={handleCellGridScroll}
                  >
                    <div className="cip-grid">
                      {visibleSegs.map(({ item, idx, grade }) => (
                        <button
                          key={idx}
                          type="button"
                          className={`cip-tile cip-tile--${grade}`}
                          onClick={() => {
                            // Grad-CAM 선택은 유지한다. 장을 넘길 때마다 꺼지면 켜 둔 의미가 없다.
                            setCellPreviewIndex(idx);
                            setIsCellZoomOpen(true);
                          }}
                          aria-label={t("map.preview.tileAria", { n: idx + 1 })}
                        >
                          {/* loading="lazy" 를 빼지 말 것. 크롭은 리사이즈 없이
                              저장돼서, 35장을 한꺼번에 받으면 열 때 눈에 띄게 느리다. */}
                          <img
                            src={
                              (isGradCam && item.gradcam) ||
                              item.crop ||
                              item.gradcam
                            }
                            alt=""
                            loading="lazy"
                          />
                          {grade !== "ok" && item.conf != null && (
                            <span className="cip-pct">{item.conf}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="cip-foot">
                    <span>
                      {hasSegmentGrades
                        ? t("map.preview.hint")
                        : cellStatusNote || t("map.preview.hint")}
                    </span>
                    {cellCoordText && (
                      <span className="cip-coord">{cellCoordText}</span>
                    )}
                  </div>
                </>
              )}

              {isCellZoomOpen && zoomSeg && (
                <div className="cip-zoom">
                  <div className="cip-zbar">
                    <button
                      type="button"
                      className="cip-back"
                      onClick={() => setIsCellZoomOpen(false)}
                    >
                      {"◂"} {t("map.preview.backToList")}
                    </button>
                    <span className="cip-pos">{zoomPosText}</span>
                    {/* 한 장씩 볼 때도 바로 켜고 끌 수 있게 여기에도 둔다. 목록 위 토글과 같은 값이라
                        여기서 바꾸면 목록으로 돌아가도 그대로 유지된다. */}
                    <label className={`cip-gcam${isGradCam ? " is-on" : ""}`}>
                      <input
                        type="checkbox"
                        checked={isGradCam}
                        onChange={(e) => setIsGradCam(e.target.checked)}
                      />
                      <span className="cip-track" />
                      {t("map.preview.gradcam")}
                    </label>
                  </div>
                  <div className="cip-zframe">
                    <img
                      src={
                        (isGradCam && zoomSeg.item.gradcam) ||
                        zoomSeg.item.crop ||
                        zoomSeg.item.gradcam
                      }
                      alt=""
                    />
                    {visibleSegs.length > 1 && (
                      <>
                        <button
                          type="button"
                          className="cip-nav l"
                          onClick={() => stepCellPreview(-1)}
                          aria-label={t("map.preview.prev")}
                        >
                          {"◀"}
                        </button>
                        <button
                          type="button"
                          className="cip-nav r"
                          onClick={() => stepCellPreview(1)}
                          aria-label={t("map.preview.next")}
                        >
                          {"▶"}
                        </button>
                      </>
                    )}
                  </div>
                  <div className="cip-zmeta">
                    {zoomSeg.item.diagnoses?.length > 1 && (
                      <span className="cip-znote">
                        {zoomSeg.item.diagnoses.map((d) => `${d.name}${d.conf == null ? "" : ` ${d.conf}%`}`).join(" · ")}
                      </span>
                    )}
                    {isGradCam && zoomSeg.item.cropObjectKey && zoomSeg.item.gradcam && (
                      <span className="cip-znote">
                        {t("map.preview.gradcamFor", { name: zoomSeg.item.gradcamName })}
                      </span>
                    )}
                    {zoomSeg.item.name ? (
                      <>
                        <span
                          className={`cip-grade cip-grade--${zoomSeg.grade}`}
                        >
                          <i />
                          {zoomSeg.grade === "ok"
                            ? t("map.grade.ok")
                            : `${zoomSeg.item.name}${
                                zoomSeg.item.conf == null
                                  ? ""
                                  : ` ${zoomSeg.item.conf}%`
                              }`}
                        </span>
                        <span className="cip-znote">
                          {t(`map.gradeNote.${zoomSeg.grade}`)}
                        </span>
                      </>
                    ) : (
                      <>
                        <span
                          className={`cip-grade cip-grade--${cellFallbackGrade}`}
                        >
                          <i />
                          {cellStatusLabel || t(`map.grade.${cellFallbackGrade}`)}
                        </span>
                        {cellStatusNote && (
                          <span className="cip-znote">{cellStatusNote}</span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <NDVIDateModal
          isOpen={isDateOpen}
          value={selectedDate}
          missionDates={missionDates}
          onChange={(date) => {
            if (!date) return;
            lockMissionSelection();
            setSelectedDate(date);
          }}
          onClose={() => setIsDateOpen(false)}
          onApply={() => {
            lockMissionSelection();
            setIsDateOpen(false);
            if (!isDemo) {
              setSelectedMissionLogId(null);
              setSelectedMissionTime("");
              setMissionTimeList([]);
            }
            setHasAppliedDate(true);
            fetchAnalysisCellIds();
          }}
        />
      </div>
    </div>
  );
});

export default DroneMap;








